/**
 * selectionProperties.ts - Set or delete cell properties on every selected cell.
 *
 * Changing an evaluable property (see {@link EVALUABLE_CELL_PROPERTIES}) re-evaluates the touched
 * cells through an `update-cells` op; any other property persists with a `noop` op.
 */

import assert from 'assert';
import produce from 'immer';

import {EVALUABLE_CELL_PROPERTIES, type FormatPropertyChange} from 'notebook/tables/constants/CellConstants';
import type {Table} from 'notebook/tables/constants/TableConstants';
import type {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import type {TableOpDiff, UpdateCellsTableOp} from 'notebook/tables/excess/ops/TableOpConstants';
import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {persistTable} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection} from 'notebook/tables/store/logic/mutations/selection';

/**
 * Apply `updates` to every existing cell inside `box`; ghost cells outside the table are skipped. A
 * `null` box selects nothing, so the table is returned unchanged with a `noop` op.
 */
export function _setSelectionPropertiesTransform(
  table: Table,
  box: MultiSelectionBox | null,
  updates: Array<FormatPropertyChange>
): {table: Table; tableOp: TableOpDiff} {
  const tableOp: UpdateCellsTableOp = {type: 'update-cells', cells: []};
  if (box == null) {
    return {table, tableOp: {type: 'noop'}};
  }
  const {minI, maxI, minJ, maxJ} = box;
  const newTable = produce(table, (draftTable) => {
    draftTable.table.forEach((draftRow, i) => {
      if (i < minI || i > maxI) {
        return;
      }
      draftRow.forEach((draftCell, j) => {
        if (j < minJ || j > maxJ) {
          return;
        }
        for (const {property, value} of updates) {
          if (EVALUABLE_CELL_PROPERTIES.includes(property)) {
            tableOp.cells.push({row: i, column: j});
          }
          if (value != null) {
            // The `property` string is not typesafe
            // @ts-expect-error
            draftCell[property] = value;
          } else {
            // @ts-expect-error
            delete draftCell[property];
          }
        }
      });
    });
  });
  return {table: newTable, tableOp: tableOp.cells.length > 0 ? tableOp : {type: 'noop'}};
}

/**
 * Checkpoint the selection for undo, then apply `updates` to every cell in the merge-expanded
 * selection. Without a multi-selection nothing is selected, but the unchanged table still persists
 * with a `noop` op.
 */
export function setSelectionProperties(
  params: TableMutationParams,
  updates: Array<FormatPropertyChange>
): void {
  const {selection} = params.getTableModel();
  if (selection.hasMultiSelection) {
    assert(selection.expandedBox != null, 'Expected to have a selection');
  }
  const box = selection.hasMultiSelection ? selection.expandedBox : null;
  const {table} = params.getTableProps();
  saveSelection(params);
  persistTable(params, _setSelectionPropertiesTransform(table, box, updates));
}

/**
 * Set (or clear, when `staticId` is `null`) the selector on every selected cell.
 */
export function setSelectionSelector(params: TableMutationParams, staticId: string | null): void {
  setSelectionProperties(params, [{property: 'selectorId', value: staticId}]);
}
